import { WarningIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { useSubscriberInternalNoteFormik } from "@modules/programs/programSubscriber/formik/useSubscriberInternalNoteFormik"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { ChevronLeft } from "lucide-react"
import { useTheme } from "styled-components"

type ProgramSubscriberInternalNoteFormProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  moveToSubscriberDetails: () => void
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriberInternalNoteForm({
  subscriberDetails,
  moveToSubscriberDetails,
  programId,
  className,
  ...props
}: ProgramSubscriberInternalNoteFormProps) {
  const defaultClassName = "flex-grow-1 flex flex-column justify-content-between gap-5 p-2 pt-5"
  const theme = useTheme()

  const { formik, isSubmitting } = useSubscriberInternalNoteFormik(
    programId,
    subscriberDetails,
    moveToSubscriberDetails,
  )

  return (
    <div className={className ?? defaultClassName} {...props}>
      {/* Form header */}
      <div className="flex flex-column gap-3 align-items-center">
        <div className="flex flex-row justify-content-center w-full relative">
          <HUButton
            type="Left_Icon"
            size="XS"
            colorType="Underline"
            text={word("global.return")}
            icon={{ iconView: <ChevronLeft color={theme.neutralColor600} size={13} /> }}
            onClick={moveToSubscriberDetails}
            textColor={theme.neutralColor600}
            className="absolute left-0"
          />

          <HUText fontStyle="BL" className="text-center">
            {word("program.subscribers.subscriptionDetails.internalNote.title")}
          </HUText>
        </div>

        <div className="flex flex-row align-items-center gap-2">
          <div>
            <WarningIcon color={theme.neutralColor600} width={20} height={20} />
          </div>
          <HUText fontStyle="BM" className="text-center" color={theme.neutralColor600}>
            {word("program.subscribers.subscriptionDetails.internalNote.description")}
          </HUText>
        </div>
      </div>

      {/* Form */}
      <div className="flex-grow-1">
        <FormikInput
          type="textarea"
          formikStyle={{ flexGrow: 1, height: "8rem" }}
          placeHolder={word("program.subscribers.subscriptionDetails.internalNote.placeholder")}
          getFieldProps={formik.getFieldProps}
          isInvalid={isFormFieldInValid("internalNote", formik)}
          name="internalNote"
          error={getFormErrorMessage("internalNote", formik)}
        />
      </div>

      {/* Submit button */}
      <div className="flex flex-row align-items-center mt-auto gap-6">
        <HUButton
          type="Default"
          colorType="Quaternary"
          size="M"
          text={word("global.cancel")}
          className="flex-1"
          onClick={moveToSubscriberDetails}
          disabled={isSubmitting}
        />
        <HUButton
          type="Default"
          colorType="Primary"
          size="M"
          text={word("global.save")}
          className="flex-1"
          onClick={formik.submitForm}
          disabled={isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </div>
  )
}
